/**
 * TraceFilters Component
 *
 * Filter chips for activity types and search box.
 * Filters the trace view without switching context.
 */

import { Component, For, Show, Accessor } from 'solid-js';
import type { AgentInfo } from '../../../stores/executionStore.ts';
import { getAgentColorById } from '../AgentFilter.tsx';

export type ActivityFilter = 'all' | 'thinking' | 'tools' | 'llm' | 'errors';

export interface TraceFiltersProps {
  activeFilter: ActivityFilter;
  onFilterChange: (filter: ActivityFilter) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  agents: AgentInfo[];
  selectedAgentId: string | null;
  onAgentSelect: (agentId: string | null) => void;
  counts: {
    all: number;
    thinking: number;
    tools: number;
    llm: number;
    errors: number;
  };
}

const filters: { id: ActivityFilter; label: string; icon: string }[] = [
  { id: 'all', label: 'All', icon: '' },
  { id: 'thinking', label: 'Thinking', icon: '' },
  { id: 'tools', label: 'Tools', icon: '' },
  { id: 'llm', label: 'LLM', icon: '' },
  { id: 'errors', label: 'Errors', icon: '' },
];

const TraceFilters: Component<TraceFiltersProps> = (props) => {
  const getFilterClass = (filter: ActivityFilter) => {
    const base = 'px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5';
    if (props.activeFilter === filter) {
      if (filter === 'errors') {
        return `${base} bg-red-500/20 text-red-300 border border-red-500/40`;
      }
      return `${base} bg-purple-500/20 text-purple-300 border border-purple-500/40`;
    }
    return `${base} bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-gray-300`;
  };

  const getIcon = (filter: ActivityFilter) => {
    switch (filter) {
      case 'thinking':
        return (
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'tools':
        return (
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'llm':
        return (
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        );
      case 'errors':
        return (
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div class="flex flex-wrap items-center gap-3 mb-4">
      {/* Activity type filters */}
      <div class="flex items-center gap-2">
        <For each={filters}>
          {(filter) => (
            <button
              type="button"
              onClick={() => props.onFilterChange(filter.id)}
              class={getFilterClass(filter.id)}
            >
              {getIcon(filter.id)}
              {filter.label}
              <Show when={props.counts[filter.id] > 0}>
                <span class="text-xs opacity-70">
                  {props.counts[filter.id]}
                </span>
              </Show>
            </button>
          )}
        </For>
      </div>

      {/* Agent filter (when multiple agents) */}
      <Show when={props.agents.length > 1}>
        <div class="h-6 w-px bg-gray-700" />
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={() => props.onAgentSelect(null)}
            class={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              props.selectedAgentId === null
                ? 'bg-gray-700 text-gray-200 border border-gray-600'
                : 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700'
            }`}
          >
            All Agents
          </button>
          <For each={props.agents}>
            {(agent) => {
              const color = getAgentColorById(agent.id, props.agents);
              return (
                <button
                  type="button"
                  onClick={() => props.onAgentSelect(agent.id)}
                  class={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    props.selectedAgentId === agent.id
                      ? `${color?.bg || 'bg-gray-700'} ${color?.text || 'text-gray-200'} ${color?.border || 'border-gray-600'} border`
                      : 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700'
                  }`}
                >
                  {agent.name || agent.id.slice(0, 8)}
                  <span class="ml-1 text-xs opacity-70">{agent.eventCount}</span>
                </button>
              );
            }}
          </For>
        </div>
      </Show>

      {/* Search box */}
      <div class="ml-auto relative">
        <svg
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search..."
          value={props.searchQuery}
          onInput={(e) => props.onSearchChange(e.currentTarget.value)}
          class="pl-9 pr-4 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30 w-48"
        />
      </div>
    </div>
  );
};

export default TraceFilters;
